"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { SHELL_BANNER_CTA, ShellBanner } from "@/components/shell/AppPromoBanner";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/contexts/toast-context";
import { usePushSubscription } from "@/hooks/use-push-subscription";
import { getEntitledSubscriptionSlugs } from "@/lib/billing/entitled-slugs";
import { isPushPromptSnoozed, snoozePushPrompt } from "@/lib/push/prompt-snooze";
import { cn } from "@/lib/utils";
import { phCapture } from "@/utils/analytics";

export function PushPromptBanner() {
  const { isLoading, user, subscriptions } = useAuth();
  if (isLoading || !user || !getEntitledSubscriptionSlugs(subscriptions).has("diamond")) return null;
  return <PushPrompt key={user.id} userId={user.id} />;
}

function PushPrompt({ userId }: { userId: string }) {
  const push = usePushSubscription();
  const { showToast } = useToast();
  const [dismissed, setDismissed] = useState(false);
  const snoozed = dismissed || isPushPromptSnoozed(userId);
  const [prompting, setPrompting] = useState<"prompt" | "quiet" | null>(null);
  const shownRef = useRef(false);
  const promptedRef = useRef(false);

  const install = push.support === "needs_install";
  const ready = push.support === "ready" && !push.loadFailed && push.optedIn !== null && !push.enabledOnThisDevice;
  const visible = !snoozed && (!push.busy || prompting !== null) && (ready || install);
  const on = push.enabledOnThisDevice && push.optedIn?.has("race_plus_ev") === true;

  useEffect(() => {
    if (!visible || shownRef.current) return;
    shownRef.current = true;
    phCapture("push_prompt_shown", { source: "banner", support: push.support });
  }, [visible, push.support]);

  useEffect(() => {
    if (prompting !== null) {
      promptedRef.current = true;
      return;
    }
    if (!promptedRef.current) return;
    promptedRef.current = false;
    if (on) showToast("Race to Place alerts are on for this device.", "success");
  }, [prompting, on, showToast]);

  function turnOn() {
    setPrompting(Notification.permission === "default" ? "prompt" : "quiet");
    phCapture("push_prompt_clicked", { source: "banner", support: "ready" });
    void push.enable("banner", "race_plus_ev").finally(() => setPrompting(null));
  }

  function dismiss() {
    snoozePushPrompt(userId);
    setDismissed(true);
    phCapture("push_prompt_dismissed", { support: push.support });
  }

  return (
    <ShellBanner
      open={visible}
      label="Race to Place alerts"
      dismissLabel="Dismiss alerts prompt"
      onDismiss={dismiss}
      strong={prompting === "prompt" ? "Click Allow in your browser's prompt." : "Get Race to Place +EV alerts."}
      rest={prompting !== null ? undefined : install ? "Add Upside to your Home Screen first." : "A push to this device when a new slip prices +EV."}
      action={
        install ? (
          <Link
            href="/account/notifications"
            onClick={() => phCapture("push_prompt_clicked", { source: "banner", support: "needs_install" })}
            className={SHELL_BANNER_CTA}
          >
            See how
          </Link>
        ) : (
          <button type="button" disabled={prompting !== null} onClick={turnOn} className={cn(SHELL_BANNER_CTA, "disabled:opacity-50")}>
            Turn on alerts
          </button>
        )
      }
    />
  );
}
